import { z } from 'zod';

/**
 * A callable contract pairs a Cloud Function name with its input/output Zod schemas.
 * Both frontend and backend import this same object as the single source of truth.
 */
export interface CallableContract<
  TName extends string,
  TInput extends z.ZodType,
  TOutput extends z.ZodType,
> {
  readonly name: TName;
  readonly input: TInput;
  readonly output: TOutput;
}

/**
 * The raw input type accepted by a contract's input schema (before transforms).
 */
export type ContractInput<C extends CallableContract<string, z.ZodType, z.ZodType>> = z.input<
  C['input']
>;

/**
 * The parsed output type produced by a contract's output schema (after transforms).
 */
export type ContractOutput<C extends CallableContract<string, z.ZodType, z.ZodType>> = z.output<
  C['output']
>;

/**
 * Creates a callable contract binding a function name to its input/output schemas.
 * The generic parameters are inferred from the arguments.
 */
export function defineCallable<
  TName extends string,
  TInput extends z.ZodType,
  TOutput extends z.ZodType,
>(
  name: TName,
  schemas: { input: TInput; output: TOutput },
): CallableContract<TName, TInput, TOutput> {
  return { name, input: schemas.input, output: schemas.output };
}
